// src/components/Leaderboard.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { apiClient } from '../api/apiClient'; // 确保你有一个配置好的axios实例
import { FaTimes } from 'react-icons/fa';
import { CornerCard } from '../components/ui';
import { color, font } from '../theme/tokens';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(-16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(7, 2, 5, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const LeaderboardContainer = styled(CornerCard)`
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  color: ${color.text};
  animation: ${slideIn} 0.35s ease-out;
`;

const Title = styled.h2`
  margin: 0 0 20px;
  padding: 0 40px 16px 0;
  border-bottom: 1px solid ${color.line};
  font-family: ${font.display};
  font-weight: 800;
  font-size: 24px;
  letter-spacing: 0.16em;
  color: ${color.text};
  span { margin-left: 12px; font-size: 14px; font-weight: 400; letter-spacing: 0.2em; color: ${color.text3}; }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid ${color.line2};
    border-radius: 0;
    color: ${color.text2};
    font-size: 14px;
    cursor: pointer;
    transition: color 0.2s ease, border-color 0.2s ease;
    &:hover {
        color: ${color.text};
        border-color: ${color.bloodHi};
    }
`;

const ScoreList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ScoreItem = styled.li`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 6px;
  border-bottom: 1px solid ${color.line};
  font-size: 15px;
  &:last-child { border-bottom: none; }
`;

const Rank = styled.span`
    flex-basis: 15%;
    font-family: ${font.mono};
    font-size: 14px;
    color: ${({ $top }) => ($top ? color.bloodHi : color.text3)};
    text-align: center;
`;
const Username = styled.span`
    flex-basis: 55%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${color.text};
`;
const Score = styled.span`
    flex-basis: 30%;
    text-align: right;
    color: ${color.text2};
    b { font-family: ${font.mono}; font-weight: 500; color: ${color.textBright}; margin-right: 4px; }
`;

const Message = styled.p`
    margin: 0;
    padding: 32px 0;
    text-align: center;
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.2em;
    color: ${({ $error }) => ($error ? color.bloodHi : color.text3)};
`;

function Leaderboard({ gameName, onClose }) {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchScores = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get(`/scores/${gameName}?limit=10`);
                setScores(response.data);
                setError(null);
            } catch (err) {
                setError('无法加载排行榜数据。');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchScores();
    }, [gameName]);

    return (
        <Overlay onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="leaderboard-title"
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', maxWidth: 500 }}
            >
                <LeaderboardContainer pad="28px 32px">
                    <CloseButton type="button" onClick={onClose} aria-label="关闭"><FaTimes /></CloseButton>
                    <Title id="leaderboard-title">番付<span>{gameName} 排行榜</span></Title>
                    {loading && <Message>载入中…</Message>}
                    {error && <Message $error role="alert">{error}</Message>}
                    {!loading && !error && (
                        <ScoreList>
                            {scores.length > 0 ? scores.map((entry, index) => (
                                <ScoreItem key={index}>
                                    <Rank $top={index < 3}>{String(index + 1).padStart(2, '0')}</Rank>
                                    <Username>{entry.username}</Username>
                                    <Score><b>{entry.score.toLocaleString()}</b>分</Score>
                                </ScoreItem>
                            )) : (
                                <Message as="li">还没有人上榜，快来成为第一名！</Message>
                            )}
                        </ScoreList>
                    )}
                </LeaderboardContainer>
            </div>
        </Overlay>
    );
}

export default Leaderboard;
